import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Project, useGetProjectQuery } from '@graphql/generated';
import { useGetUserAvatarsByIdsQuery } from '@hooks/useGetUserAvatarsByIdsQuery';
import {
  logError,
  LogNetworkError,
  LogGraphError,
} from '@utils/logNonFatalError';
import { getRenderableProjectMembers } from '@utils/projects';

/**
 * Retrieves a project for the provided `projectId`
 */
export const useProjectFromQuery = ({
  projectUUId,
  fetchPolicy = 'cache-first',
}: {
  projectUUId: Project['uuid'];
  fetchPolicy?: WatchQueryFetchPolicy;
}) => {
  const isIntegerId = projectUUId ? /^\d+$/.test(projectUUId) : false;
  const isValidId = projectUUId
    ? isIntegerId ||
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        projectUUId
      )
    : false;

  const [retryCount, setRetryCount] = useState(0);
  const [shouldRetry, setShouldRetry] = useState(false);

  const {
    data: { getProject } = {},
    loading,
    error,
    refetch,
  } = useGetProjectQuery({
    variables: {
      id: projectUUId,
    },
    skip: !projectUUId || !isValidId,
    fetchPolicy,
  });

  // The backend can return members[].user: null (and, by the same TA-10106
  // gating, memberPreviews[].user: null) for a team-visible project the
  // current user isn't an explicit member of; codegen still types both
  // non-null (TA-10297). Sanitize once here so the ~35 getProject consumers
  // don't each have to guard the element, and so both arrays are always an
  // array (never null/undefined) whenever `project` is truthy. Returns the
  // Apollo object untouched only when nothing was dropped from either array
  // AND both were already arrays, so referential stability is unchanged for
  // healthy payloads.
  const project = useMemo(() => {
    if (!getProject) return undefined;
    const members = getRenderableProjectMembers(getProject.members);
    const memberPreviews = getRenderableProjectMembers(
      getProject.memberPreviews
    );
    // Gate on the source field actually being an array, not just `?? []`
    // matching post-sanitize length — an absent/null field would otherwise
    // match a `[]` sanitize result and skip normalization, leaving `members`/
    // `memberPreviews` as undefined on the returned object and reintroducing
    // the exact crash this hook exists to prevent.
    const membersUnchanged =
      Array.isArray(getProject.members) &&
      members.length === getProject.members.length;
    const memberPreviewsUnchanged =
      Array.isArray(getProject.memberPreviews) &&
      memberPreviews.length === getProject.memberPreviews.length;
    if (membersUnchanged && memberPreviewsUnchanged) {
      return getProject;
    }
    return { ...getProject, members, memberPreviews };
  }, [getProject]);

  useEffect(() => {
    if (!error) return;
    if (error.networkError) {
      const event: LogNetworkError = {
        message: 'Project Network Failure',
        errorMessage: error.message,
        statusCode:
          'statusCode' in error.networkError
            ? error.networkError.statusCode
            : -1,
        extra: [{ projectUUId }],
      };
      logError(event);
    }
    error.graphQLErrors.forEach((graphError) => {
      const event: LogGraphError = {
        message: 'Project GraphQL Error',
        errorMessage: graphError.message,
        operationName: 'getProject',
        extra: [{ projectUUId }],
      };
      if (graphError.message.includes('not found') && retryCount < 3) {
        setShouldRetry(true);
      }
      logError(event);
    });
  }, [error, projectUUId]);

  useEffect(() => {
    if (shouldRetry && retryCount < 3) {
      const delay = Math.pow(2, retryCount) * 1000;
      const timer = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        setShouldRetry(false);
        refetch?.();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [shouldRetry, retryCount, refetch]);

  useEffect(() => {
    if (getProject && retryCount > 0) {
      setRetryCount(0);
    }
  }, [getProject, retryCount]);

  const { checkUserAvatarURLByIds } = useGetUserAvatarsByIdsQuery();
  // Ref so the effect always calls the latest instance without depending on it
  // (the hook returns a new function each render).
  const checkUserAvatarURLByIdsRef = useRef(checkUserAvatarURLByIds);
  checkUserAvatarURLByIdsRef.current = checkUserAvatarURLByIds;

  // Key the avatar check on the set of member avatar IDs rather than the
  // getProject object reference, so it only fires when the membership actually
  // changes. (TA-9793)
  const projectAvatarIdsKey = useMemo(() => {
    const avatarIds = new Set<string>();
    project?.members?.forEach((p) => {
      if (p.user.avatar?.id) {
        avatarIds.add(p.user.avatar.id);
      }
    });
    return [...avatarIds].sort((a, b) => a.localeCompare(b)).join(',');
  }, [project?.members]);

  useEffect(() => {
    if (!projectAvatarIdsKey) return;
    checkUserAvatarURLByIdsRef.current(projectAvatarIdsKey.split(','));
  }, [projectAvatarIdsKey]);

  useEffect(() => {
    if (isIntegerId && __DEV__) {
      // Integer IDs are supported but UUID format is preferred
      // eslint-disable-next-line no-console
      console.warn(
        `useProjectFromQuery: projectUUId "${projectUUId}" is an integer, UUID format preferred`
      );
    }
  }, [isIntegerId, projectUUId]);

  return {
    project: project as Project | undefined,
    loading,
    error,
    refetch,
  };
};
